<template>
  <div class="view box grow" v-loading="isLoading">
    <page-headers
      :header="'Threat Progressions'"
      :breadcrumbs="breadcrumbs"
      :is-border="true"
      documentation="page-progressions"
      class="box"
    >
    </page-headers>
    <div v-if="trailsinfoFingerprintReqStatus === 'saved'">
      <table-toolbar
        :key="trailInfoCollection.key"
        :search-properties="visibleTrailColumns"
        :total-search-properties="trailColumns"
        :search-data="trailInfoCollection"
        :initial-tags="trailInfoCollection.searchTags"
        :reload-component="trailInfoCollection.reloadToolbar"
        @update-querydata="updateTableQueryData"
        @refresh-tabledata="refreshTableData"
        @update-columns-data="modifyColumnsData"
      >
        <div slot="local-helper" class="search-reset">
          <div class="grid-content helper-message">
            <p>Showing Progressions for Fingerprint {{ fngerprintLabel }}</p>
            <el-button type="text" class="icon-text-button" @click="gotoParentPage">
              <i class="material-icons c-icon"></i><span>Reset</span>
            </el-button>
          </div>
        </div>
      </table-toolbar>
    </div>

    <multi-select-action-table-element
      v-if="trailsinfoFingerprintReqStatus == 'saved'"
      ref="attacksTable"
      :columns="visibleTrailColumns"
      :table-data="trailInfoCollection"
      :height-offset="295"
      :actions="tableActions"
      @update-record-count="recordCountHandler"
      @table-selection-change="canMitigateSelectedProgressions"
    ></multi-select-action-table-element>

    <el-dialog title="Assign Threat Progression" :visible.sync="openAssignToDialogVisible">
      <el-form :model="selectedAttackTrail" v-if="usersListStatus == 'saved'">
        <div class="body1 el-form-item">
          Assignee:
          <user-button
            :classList="selectedAttackTrail.ownerId ? 'shamrock-200' : ''"
            :user="selectedAttackTrail.ownerId"
          ></user-button>
          {{ selectedAttackTrail.ownerId ? selectedAttackTrail.ownerId.fullname : 'Unassigned' }}
        </div>
        <el-form-item label="Assign to" prop="email">
          <el-select v-model="selectedAttackTrail.assignTo" filterable placeholder="Assign to...">
            <el-option
              v-for="item in usersList.models"
              :key="item.email"
              :label="item.firstname + ' ' + item.lastname"
              :value="item.email"
            ></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="Comments">
          <el-input
            type="textarea"
            rows="5"
            placeholder="Please input"
            v-model="selectedAttackTrail.comments"
          ></el-input>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="openAssignToDialogVisible = false">Cancel</el-button>
        <el-button type="primary" @click="assignTrailWork(selectedAttackTrail)">Confirm</el-button>
      </span>
    </el-dialog>

    <el-dialog title="Mitigate the Selected Progression(s)" :visible.sync="openMitigateDialogVisible">
      <el-form>
        <label>
          Are you sure you want to mitigate the following progressions?
        </label>

        <h6><br />Progressions selected:</h6>
        <span v-for="(progression, idx) in selectedProgressions" :key="idx">
          {{ progression.trailIdLabel }}
          <el-divider v-if="idx !== selectedProgressions.length - 1" direction="vertical"></el-divider>
        </span>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button
          @click="
            openMitigateDialogVisible = false
            disabledMitigateSelectedProgressions = false
          "
          >Cancel</el-button
        >
        <el-button type="primary" @click="mitigateSelectedProgressions">Confirm</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import {
  TRAILSINFO_FINGERPRINT_REQUEST,
  TRAILSINFO_FINGERPRINT_SUCCESS,
  BULK_TRAIL_MITIGATION_REQ,
  TRAIL_ASSIGN_REQ
} from '@/store/modules/trails/trailsinfo.type'
import { USER_LIST_REQUEST, USER_LIST_SUCCESS } from '@/store/modules/user/user.type'
import { THREAT_PROGRESSION_DATE_RANGE_PREFERENCE } from '@/store/modules/user_pref/user-pref.type'

import { trailsMetaInfo } from '@/column-header/trail/trails-meta'

import PageHeaders from '@/components/layout/PageHeaders.vue'
import LineChartJs from '@/components/chart/LineChartJs.vue'
import DonutChartJs from '@/components/chart/DonutChartJs.vue'
import MultiSelectActionTableElement from '@/components/table/MultiSelectActionTableElement.vue'
import TableToolbar from '@/components/table/TableToolbar.vue'
import UserButton from '@/components/button/UserButton.vue'
import DateRangeDropdown from '@/components/dropdown/DateRangeDropdown.vue'

export default {
  name: 'TrailFingerPrint',
  data () {
    return {
      breadcrumbs: [{ title: 'Threats' }, { title: 'Progressions' }],
      daterangeUserPrefConstant: THREAT_PROGRESSION_DATE_RANGE_PREFERENCE,
      trailColumns: trailsMetaInfo.meta.columns,
      visibleTrailColumns: trailsMetaInfo.meta.columns,
      trailInfoCollection: {},
      selectedAttackTrail: {},
      selectedProgressions: [],
      isLoading: false,
      openAssignToDialogVisible: false,
      openMitigateDialogVisible: false,
      disabledMitigateSelectedProgressions: true,
      tableActions: [
        {
          name: 'handoff',
          icon: 'material-icons c-icon',
          iconSubject: 'person_add_alt_1',
          tooltip: 'Handoff Progression',
          rbac: {
            subject: 'cyber_attacks',
            operation: 'write'
          },
          actionHandler: selection => {
            this.handoffTrail(selection.row)
          }
        }
      ]
    }
  },
  methods: {
    getTrailListInfo (tableMetaData) {
      this.isLoading = true
      this.$store.dispatch(TRAILSINFO_FINGERPRINT_REQUEST, this.fngerprintLabel).then(
        trails => {
          this.isLoading = false
          this.trailInfoCollection = this.trailsinfoFingerprintRecords
          this.$store.dispatch(TRAILSINFO_FINGERPRINT_SUCCESS)
          this.setTableMetaData(tableMetaData)
        },
        errMsg => {
          this.isLoading = false
          console.log(errMsg)
        }
      )
    },
    gotoDetails (trailId) {
      this.$router.push({
        name: 'attack-track-details',
        params: { trailId: trailId }
      })
    },
    handoffTrail (trail) {
      this.openAssignToDialogVisible = true
      this.selectedAttackTrail = trail
    },
    assignTrailWork (selectedTrail) {
      this.$store
        .dispatch(TRAIL_ASSIGN_REQ, {
          assignFrom: this.userProfile.email,
          assignTo: selectedTrail.assignTo,
          trailId: selectedTrail.trailIdHash,
          comments: selectedTrail.comments
        })
        .then(
          () => {
            let assignedToUser = _.find(this.usersList.models, {
              email: selectedTrail.assignTo
            })
            this.selectedAttackTrail.setOwnerId(assignedToUser)
            this.openAssignToDialogVisible = false
            this.$message({
              type: 'success',
              message: `Threat Progression ${selectedTrail.trailIdHash} is now assigned to ${selectedTrail.assignTo}.`
            })
          },
          errMsg => {
            // keep open & show error
            this.$message({
              type: 'error',
              message: errMsg.data.message || `Something went wrong.`
            })
            this.openAssignToDialogVisible = true
          }
        )
    },
    assignToSelf (trail) {
      // this vue module instance
      let vm = this
      this.$confirm(`This will assign ${trail.trailIdHash} to yourself. Continue?`, 'Assign to Self', {
        confirmButtonText: 'OK',
        cancelButtonText: 'Cancel',
        callback: function (action) {
          if (action == 'confirm') {
            vm.$store
              .dispatch(TRAIL_ASSIGN_REQ, {
                assignFrom: vm.userProfile.email,
                assignTo: vm.userProfile.email,
                trailId: trail.trailIdHash
              })
              .then(
                () => {
                  vm.$message({
                    type: 'success',
                    message: `Threat Progression ${trail.trailIdHash} is now assigned to you.`
                  })
                },
                errMsg => {}
              )
          } else if (action == 'cancel') {
            vm.$message({
              type: 'info',
              message: `Assignment canceled`
            })
          }
        }
      })
    },
    updateTableQueryData () {
      if (this.$refs.attacksTable) {
        this.$refs.attacksTable.updateTableData()
      }
    },
    recordCountHandler () {
      // key is used to re-render TableToolbar component
      // on page change to update 'queryCountText'
      this.trailInfoCollection.key = !this.trailInfoCollection.key
      this.trailInfoCollection.reloadToolbar = false
    },
    modifyColumnsData (selectedColumns) {
      this.visibleTrailColumns = this.trailInfoCollection.updateVisibleColumns(this.trailColumns, selectedColumns)
    },
    callApi (tableMetaData) {
      if (this.usersListStatus !== 'saved') {
        this.$store.dispatch(USER_LIST_REQUEST).then(data => {
          this.$store.dispatch(USER_LIST_SUCCESS)
        })
      }

      this.getTrailListInfo(tableMetaData)
    },
    setTableMetaData (tableMetaData) {
      this.trailInfoCollection.searchTags = tableMetaData.searchTags
      this.trailInfoCollection.currentPage = tableMetaData.activePage
    },
    refreshTableData (tableMetaData) {
      this.callApi(tableMetaData)
    },
    canMitigateSelectedProgressions () {
      if (this.$refs.attacksTable.rowsSelected.length > 0) {
        this.disabledMitigateSelectedProgressions = false
      } else {
        this.disabledMitigateSelectedProgressions = true
      }
    },
    openMitigateDialog () {
      this.selectedProgressions = this.$refs.attacksTable.rowsSelected
      this.openMitigateDialogVisible = true
    },
    mitigateSelectedProgressions () {
      this.disabledMitigateSelectedProgressions = true
      let email = this.userProfile.email
      let trailIds = []

      this.$refs.attacksTable.rowsSelected.forEach(trail => {
        trailIds.push(trail.id)
      })

      this.$store
        .dispatch(BULK_TRAIL_MITIGATION_REQ, {
          assignFrom: email,
          assignTo: email,
          trails: trailIds
        })
        .then(() => {
          this.disabledMitigateSelectedProgressions = false
          this.openMitigateDialogVisible = false
          this.refreshTableData({
            activePage: 1,
            searchTags: this.trailInfoCollection.searchTags
          })
        })
    },
    gotoParentPage () {
      this.$router.push({ name: 'active-cyber-attacks' })
    }
  },
  created () {
    let tableMetaData = {
      activePage: 1,
      searchTags: []
    }
    if (!this.$_.isEmpty(this.$route.query.date_range)) {
      this.$store.dispatch(this.daterangeUserPrefConstant, this.$route.query.date_range)
    }
    this.callApi(tableMetaData)
  },
  watch: {
    trailProgressionDateRangeUserSetting: {
      handler: function (newDateRange, oldDateRange) {
        if (newDateRange !== oldDateRange) {
          this.refreshTableData({
            activePage: 1,
            searchTags: this.trailInfoCollection.searchTags
          })
        }
      }
    }
  },
  computed: {
    ...mapGetters([
      'trailsinfoFingerprintReqStatus',
      'trailsinfoFingerprintRecords',
      'userProfile',
      'usersList',
      'usersListStatus',
      'trailsAssignmentStatus',
      'trailProgressionDateRangeUserSetting'
    ]),
    fngerprintLabel () {
      return this.$route.params.fingerprintId
    }
  },
  components: {
    PageHeaders,
    LineChartJs,
    DonutChartJs,
    MultiSelectActionTableElement,
    TableToolbar,
    UserButton,
    DateRangeDropdown
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
@import 'assets/styles/_variables.css';
.el-col {
  padding-bottom: 8px;
}

.el-card {
  padding-top: 5px;
}

.trails-info {
  max-width: 65%;
  > span {
    display: block;
    padding: 2px;
  }
}

.title {
  font-size: 0.9em;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.info {
  font-size: 0.8em;
}

.trail-icon {
  transform: rotate(90deg);
}

.material-icons {
  font-size: 2em;
  margin-left: 10px;
}

.el-dropdown-menu.el-popper {
  .material-icons {
    font-size: 1em;
    margin-left: 0px;
  }
}

.el-card__body {
  padding: none !important;

  > div {
    margin: 5px;
  }
}

.circle {
  width: 24px;
  height: 24px;
  display: table-cell;
  border-radius: 50%;
  font-size: 15px;
  color: #fff;
  line-height: 24px;
  text-align: center;
  background: var(--primary-blue-400);
}

.el-button--mini {
  padding: 5px;
}

.view-mode {
  height: 40px;
  width: 100px;
  vertical-align: middle;
  line-height: 40px;

  .el-select:before {
    content: '\e896';
    font-family: 'Material Icons';
    font-size: 20px;
    margin-right: 16px;
    color: var(--gray-500);
    left: 50px;
    position: relative;
  }

  >>> .el-select {
    height: inherit;
    .el-input.el-input--suffix {
      visibility: hidden;
      top: -45px;
    }
  }
}

.search-box .el-button {
  padding: 9px;
  border-radius: 0;
  border: 1px solid var(--border-color);
}

.bottom {
  border-top: 1px solid #f0f0f0;
}

.clearfix:before,
.clearfix:after {
  display: table;
  content: '';
}

.clearfix:after {
  clear: both;
}

.display-none {
  display: none;
}

.search-reset {
  line-height: 36px;

  p {
    display: inline;
  }

  .icon-text-button {
    background: var(--denim-500);
    padding: 5px 10px;
    vertical-align: middle;

    span {
      color: white;
    }

    .c-icon {
      margin-right: 0px !important;
    }
  }

  .helper-message {
    background: var(--denim-50);
    padding: 20px;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/monitor/cyber_attacks/TrailsInfoFingerprint.vue